import { useState, useCallback, useRef, useEffect } from 'react';

import { searchLibraryImages, type StockItemResult } from '../../_actions/searchLibraryImages';
import type { ImageSearchMediaTypeOption, ImageSearchReturn } from '../types';

const DEBOUNCE_MS = 300;

export function useImageSearch(): ImageSearchReturn {
    const [showSearch, setShowSearch] = useState(false);
    const [searchTerm, setSearchTermState] = useState('');
    const [mediaType, setMediaTypeState] = useState<ImageSearchMediaTypeOption>('Images');
    const [results, setResults] = useState<StockItemResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [pageNumber, setPageNumber] = useState(1);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const hasMore = results.length < totalCount;

    const performSearch = useCallback(
        async (
            term: string,
            type: ImageSearchMediaTypeOption,
            page: number,
            append: boolean = false,
        ): Promise<void> => {
            // Cancel any pending request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            const controller = new AbortController();
            abortControllerRef.current = controller;

            setIsLoading(true);
            setError(null);

            try {
                const data = await searchLibraryImages(term, type, page);

                // Check if THIS request was aborted (not the current ref, which may be a newer request)
                if (controller.signal.aborted) {
                    return;
                }

                if (append) {
                    setResults((prev) => [...prev, ...data.stockItems]);
                } else {
                    setResults(data.stockItems);
                }
                setTotalCount(data.totalCount);
                setTotalPages(data.totalPages);
                setPageNumber(page);
                setHasSearched(true);
            } catch (err) {
                if (err instanceof Error && err.name === 'AbortError') {
                    return;
                }
                setError('Failed to search images. Please try again.');
                setHasSearched(true);
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            }
        },
        [],
    );

    const search = useCallback(async (): Promise<void> => {
        await performSearch(searchTerm, mediaType, 1, false);
    }, [searchTerm, mediaType, performSearch]);

    const loadMore = useCallback(async (): Promise<void> => {
        if (isLoading || !hasMore) return;
        await performSearch(searchTerm, mediaType, pageNumber + 1, true);
    }, [isLoading, hasMore, searchTerm, mediaType, pageNumber, performSearch]);

    const setSearchTerm = useCallback(
        (term: string): void => {
            setSearchTermState(term);

            // Clear any existing debounce timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // Debounce the search
            debounceTimerRef.current = setTimeout(() => {
                void performSearch(term, mediaType, 1, false);
            }, DEBOUNCE_MS);
        },
        [mediaType, performSearch],
    );

    const setMediaType = useCallback(
        (type: ImageSearchMediaTypeOption): void => {
            setMediaTypeState(type);
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            void performSearch(searchTerm, type, 1, false);
        },
        [searchTerm, performSearch],
    );

    const goToPage = useCallback(
        async (page: number): Promise<void> => {
            if (page < 1 || page > totalPages || isLoading) return;
            await performSearch(searchTerm, mediaType, page, false);
        },
        [totalPages, isLoading, searchTerm, mediaType, performSearch],
    );

    const reset = useCallback((): void => {
        setSearchTermState('');
        setMediaTypeState('Images');
        setResults([]);
        setTotalCount(0);
        setTotalPages(1);
        setPageNumber(1);
        setError(null);
        setHasSearched(false);

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return {
        searchTerm,
        setSearchTerm,
        mediaType,
        setMediaType,
        results,
        isLoading,
        error,
        hasSearched,
        totalCount,
        hasMore,
        loadMore,
        search,
        reset,
        currentPage: pageNumber,
        totalPages,
        goToPage,
        showSearch,
        setShowSearch,
    };
}
